import type { ContentSurroundProps } from '#ui/types';

export type UseStoryblokBlogSurroundReturn = ComputedRef<ContentSurroundProps['surround']>;

export const useStoryblokBlogSurround = async (
    pageSlug: MaybeRefOrGetter<string | undefined>,
    publishedAt?: MaybeRefOrGetter<string | undefined>,
    categoryIds?: MaybeRefOrGetter<string[] | undefined>
): Promise<UseStoryblokBlogSurroundReturn> => {
    const storyblokApi = useStoryblokApi();
    const { blogApiOptions } = useStoryblokBlogApiParams(categoryIds);

    const prevKey = computed(() => `blog-surround-prev-${toValue(pageSlug) ?? '_'}`);
    const nextKey = computed(() => `blog-surround-next-${toValue(pageSlug) ?? '_'}`);

    const [{ data: prevSurround }, { data: nextSurround }] = await Promise.all([
        useAsyncData(prevKey, () => {
            const slugValue = toValue(pageSlug);

            if (!slugValue) return Promise.resolve(null);

            return storyblokApi.getStories({
                ...blogApiOptions.value,
                excluding_slugs: slugValue,
                first_published_at_lt: toValue(publishedAt),
                per_page: 1,
            });
        }),
        useAsyncData(nextKey, () => {
            const slugValue = toValue(pageSlug);

            if (!slugValue) return Promise.resolve(null);

            return storyblokApi.getStories({
                ...blogApiOptions.value,
                excluding_slugs: slugValue,
                first_published_at_gt: toValue(publishedAt),
                per_page: 1,
            });
        }),
    ]);

    return computed(() => {
        const prevStory = prevSurround.value?.data.stories[0];
        const nextStory = nextSurround.value?.data.stories[0];

        return [
            prevStory ? { title: prevStory.content.title, description: prevStory.content.excerpt, path: storyblokGetStoryUrl(prevStory) } : null,
            nextStory ? { title: nextStory.content.title, description: nextStory.content.excerpt, path: storyblokGetStoryUrl(nextStory) } : null,
        ] as unknown as ContentSurroundProps['surround']; // Cast needed: UContentSurround's type definition doesn't accept null entries despite the component handling them correctly
    });
};
